import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "oidc-react";
import type {
  ActualsDetails,
  initialTreeValues,
  parentTreeSelection,
  RawActualsList,
  ShowData,
  Shows,
} from "components/actuals/SharedFiles/definition";
import { checkboxList } from "components/common/constants";
import { API_ENDPOINTS } from "services/apiEndPoints";
import api from "services/api";
import { getProfileData } from "utils/utils";
import AuthStatusCard from "components/common/AuthStatusCard";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import type {
  CompareScopeOverview,
  CompareToolCriteria,
  CompareToolRow,
} from "components/compareTool/compareToolTypes";

type UserProfile = {
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
};

type BreakinRecord = {
  id?: string | number;
  [key: string]: unknown;
};

const getRoleBootstrapProfile = (
  authProfile: UserProfile | null | undefined,
  storedProfile: UserProfile | null | undefined,
): UserProfile => ({
  preferred_username: authProfile?.preferred_username ?? storedProfile?.preferred_username,
  given_name: authProfile?.given_name ?? storedProfile?.given_name,
  family_name: authProfile?.family_name ?? storedProfile?.family_name,
  email: authProfile?.email ?? storedProfile?.email,
});

interface NetworkContextType {
  defaultCh: number[];
  setDefaultCh: React.Dispatch<React.SetStateAction<number[]>>;
  networkList: string[];
  setNetworkListStore: React.Dispatch<React.SetStateAction<string[]>>;
  breakinList: BreakinRecord[];
  setBreakinListStore: React.Dispatch<React.SetStateAction<BreakinRecord[]>>;
  showDetails: RawActualsList | undefined;
  setShowDetails: React.Dispatch<React.SetStateAction<RawActualsList | undefined>>;
  feedNo: string | undefined;
  setFeedNo: React.Dispatch<React.SetStateAction<string | undefined>>;
  defaultTimeZone: string | undefined;
  setDefaultTimeZone: React.Dispatch<React.SetStateAction<string | undefined>>;
  parentTreeItems?: parentTreeSelection;
  setParentTreeItems: React.Dispatch<React.SetStateAction<parentTreeSelection | undefined>>;
  expandedPanels: initialTreeValues;
  setExpandedPanels: React.Dispatch<React.SetStateAction<initialTreeValues>>;
  expandedShowData: Record<string, ShowData>;
  setExpandedShowData: React.Dispatch<React.SetStateAction<Record<string, ShowData>>>;
  innerExpanded: string | boolean;
  setInnerExpanded: React.Dispatch<React.SetStateAction<string | false>>;
  expandedShowDetails: Shows | undefined;
  setExpandedShowDetails: React.Dispatch<React.SetStateAction<Shows | undefined>>;
  actualsEventCheckBoxes: string[];
  setActualsEventCheckBoxes: React.Dispatch<React.SetStateAction<string[]>>;
  hasActualsData: boolean;
  setHasActualsData: React.Dispatch<React.SetStateAction<boolean>>;
  userRole: string | null;
  setUserRole: React.Dispatch<React.SetStateAction<string | null>>;
  bulkEditData: ActualsDetails[];
  setBulkEditData: React.Dispatch<React.SetStateAction<ActualsDetails[]>>;
  bulkEditPairs: Record<number, ActualsDetails>;
  setBulkEditPairs: React.Dispatch<React.SetStateAction<Record<number, ActualsDetails>>>;
  bulkEditSide: string;
  setBulkEditSide: React.Dispatch<React.SetStateAction<string>>;
  enableBulkEdit: boolean;
  setEnableBulkEdit: React.Dispatch<React.SetStateAction<boolean>>;
  refreshKey: number;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
  compareToolData: CompareToolRow[];
  setCompareToolData: React.Dispatch<React.SetStateAction<CompareToolRow[]>>;
  compareToolDataOverview: CompareScopeOverview[];
  setCompareToolDataOverview: React.Dispatch<React.SetStateAction<CompareScopeOverview[]>>;
  compareToolShowData: boolean;
  setCompareToolShowData: React.Dispatch<React.SetStateAction<boolean>>;
  compareToolCriteria: CompareToolCriteria | null;
  setCompareToolCriteria: React.Dispatch<React.SetStateAction<CompareToolCriteria | null>>;
  checkedBoxItems: ShowData;
  setCheckedBoxItems: React.Dispatch<React.SetStateAction<ShowData>>;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

interface NetworkProviderProps {
  children: React.ReactNode;
}

function AccessValidationStatus() {
  return (
    <AuthStatusCard
      icon={<ShieldOutlinedIcon />}
      title="Validating your access"
      message="We are checking your sign-in session and Recon permissions."
      progressLabel="Checking access..."
    />
  );
}

export const NetworkProvider = ({ children }: NetworkProviderProps) => {
  const auth = useAuth();
  const [defaultCh, setDefaultCh] = useState<number[]>([]);
  const [networkList, setNetworkListStore] = useState<string[]>([]);
  const [breakinList, setBreakinListStore] = useState<BreakinRecord[]>([]);
  const [showDetails, setShowDetails] = useState<RawActualsList>();
  const [feedNo, setFeedNo] = useState<string>();
  const [defaultTimeZone, setDefaultTimeZone] = useState<string>();
  const [parentTreeItems, setParentTreeItems] = useState<parentTreeSelection | undefined>();
  const [expandedPanels, setExpandedPanels] = useState({ network: false, bdate: false, schedule: false });
  const [expandedShowData, setExpandedShowData] = useState<Record<string, ShowData>>({});
  const [innerExpanded, setInnerExpanded] = useState<string | false>(false);
  const [expandedShowDetails, setExpandedShowDetails] = useState<Shows | undefined>();
  const [actualsEventCheckBoxes, setActualsEventCheckBoxes] = useState<string[]>([]);
  const [hasActualsData, setHasActualsData] = useState<boolean>(false);
  const hasUserCleared = useRef(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isRoleLoading, setIsRoleLoading] = useState<boolean>(() => Boolean(auth.userData));
  const [bulkEditData, setBulkEditData] = useState<ActualsDetails[]>([]);
  const [bulkEditPairs, setBulkEditPairs] = useState<Record<number, ActualsDetails>>({});
  const [bulkEditSide, setBulkEditSide] = useState("");
  const [enableBulkEdit, setEnableBulkEdit] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [compareToolData, setCompareToolData] = useState<CompareToolRow[]>([]);
  const [compareToolDataOverview, setCompareToolDataOverview] = useState<CompareScopeOverview[]>([]);
  const [compareToolShowData, setCompareToolShowData] = useState<boolean>(false);
  const [compareToolCriteria, setCompareToolCriteria] = useState<CompareToolCriteria | null>(null);
  const [checkedBoxItems, setCheckedBoxItems] = useState<ShowData>({ shows: [] });

  useEffect(() => {
    let isMounted = true;

    const fetchUserRole = async () => {
      if (auth.isLoading) {
        return;
      }

      if (!auth.userData) {
        setUserRole(null);
        setIsRoleLoading(false);
        return;
      }

      setIsRoleLoading(true);

      try {
        const profile = getRoleBootstrapProfile(
          auth.userData.profile as UserProfile | null | undefined,
          getProfileData() as UserProfile | null,
        );
        const preferredUsername = profile?.preferred_username;

        if (!preferredUsername) {
          if (isMounted) {
            setUserRole("no-access");
          }
          return;
        }

        const response = await api(
          "get",
          `${API_ENDPOINTS.USER_ROLE}?userSSO=${encodeURIComponent(preferredUsername)}`,
        );

        if (!isMounted) {
          return;
        }

        if (response?.role) {
          setUserRole(response.role);
          return;
        }

        const userData = [
          {
            firstName: profile?.given_name,
            lastName: profile?.family_name,
            userSSO: preferredUsername,
            email: profile?.email,
            role: "new-user",
          },
        ];

        await api("POST", API_ENDPOINTS.USER_ROLE, userData);

        if (isMounted) {
          setUserRole("new-user");
        }
      } catch (error) {
        if (isMounted) {
          console.error("NetworkProvider -> getUserRole(): Error fetching user role:", error);
          setUserRole("no-access");
        }
      } finally {
        if (isMounted) {
          setIsRoleLoading(false);
        }
      }
    };

    void fetchUserRole();

    return () => {
      isMounted = false;
    };
  }, [auth.isLoading, auth.userData]);

  useEffect(() => {
    if (actualsEventCheckBoxes.length === 0 && !hasUserCleared.current) {
      setActualsEventCheckBoxes(checkboxList.map((checkbox) => checkbox.key));
    }

    if (actualsEventCheckBoxes.length === 0) {
      hasUserCleared.current = true;
    }
  }, [actualsEventCheckBoxes]);

  if (auth.userData && (isRoleLoading || !userRole)) {
    return <AccessValidationStatus />;
  }

  return (
    <NetworkContext.Provider
      value={{
        defaultCh,
        setDefaultCh,
        networkList,
        setNetworkListStore,
        breakinList,
        setBreakinListStore,
        showDetails,
        setShowDetails,
        feedNo,
        setFeedNo,
        defaultTimeZone,
        setDefaultTimeZone,
        parentTreeItems,
        setParentTreeItems,
        expandedPanels,
        setExpandedPanels,
        expandedShowData,
        setExpandedShowData,
        innerExpanded,
        setInnerExpanded,
        expandedShowDetails,
        setExpandedShowDetails,
        actualsEventCheckBoxes,
        setActualsEventCheckBoxes,
        hasActualsData,
        setHasActualsData,
        userRole,
        setUserRole,
        bulkEditData,
        setBulkEditData,
        bulkEditPairs,
        setBulkEditPairs,
        bulkEditSide,
        setBulkEditSide,
        enableBulkEdit,
        setEnableBulkEdit,
        refreshKey,
        setRefreshKey,
        compareToolData,
        setCompareToolData,
        compareToolDataOverview,
        setCompareToolDataOverview,
        compareToolShowData,
        setCompareToolShowData,
        compareToolCriteria,
        setCompareToolCriteria,
        checkedBoxItems,
        setCheckedBoxItems,
      }}
    >
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetworkContext = (): NetworkContextType => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error("useNetworkContext must be used within a NetworkProvider");
  }
  return context;
};

export const useCatalogContext = () => {
  const { networkList, setNetworkListStore } = useNetworkContext();

  return {
    networkList,
    setNetworkListStore,
  };
};

export const useCompareToolContext = () => {
  const {
    compareToolData,
    setCompareToolData,
    compareToolDataOverview,
    setCompareToolDataOverview,
    compareToolShowData,
    setCompareToolShowData,
    compareToolCriteria,
    setCompareToolCriteria,
  } = useNetworkContext();

  return {
    compareToolData,
    setCompareToolData,
    compareToolDataOverview,
    setCompareToolDataOverview,
    compareToolShowData,
    setCompareToolShowData,
    compareToolCriteria,
    setCompareToolCriteria,
  };
};

export const useUserRoleContext = () => {
  const { userRole, setUserRole } = useNetworkContext();

  return {
    userRole,
    setUserRole,
  };
};
